import {breakpoints, type Breakpoint} from '@/styles/tokens';

/**
 * A `sizes` value per breakpoint, using the same keys and the same
 * mobile-first, min-width semantics as MUI's `sx` prop. Each entry applies
 * from its breakpoint upwards until the next declared one takes over.
 */
export type SizesByBreakpoint = Partial<Record<Breakpoint, string>>;

// Mirrors MUI's own `theme.breakpoints.down()`, which subtracts 0.02px so a
// `max-width` query is exactly complementary to the `min-width` query that
// `sx` emits. Without this, both rules match on the boundary pixel.
// contentWidth tokens are authored in rem against a 16px root (see tokens.ts).
const ROOT_FONT_SIZE_PX = 16;

const below = (value: number) => `(max-width: ${value - 0.02}px)`;

/**
 * Builds a next/image `sizes` string from a breakpoint map, so the rendered
 * width declared to the browser is derived from the same `breakpoints` config
 * that drives the component's `sx` — no duplicated magic numbers.
 *
 * Without an accurate `sizes`, a `fill` image defaults to `100vw` and next/image
 * fetches the largest configured variant (3840px) regardless of how small the
 * element actually renders.
 *
 * @example
 * // Box with sx={{width: {xs: 64, sm: 80, lg: 140}}}
 * imgSizes({xs: '64px', sm: '80px', lg: '140px'})
 * // → "(max-width: 599.98px) 64px, (max-width: 1199.98px) 80px, 140px"
 */
export function imgSizes(map: SizesByBreakpoint): string {
    const entries = (Object.keys(map) as Breakpoint[])
        .filter((key) => map[key] !== undefined)
        .sort((a, b) => breakpoints[a] - breakpoints[b]);

    if (entries.length === 0) {
        throw new Error('imgSizes() needs at least one breakpoint entry');
    }

    // Every entry is capped by the *next* breakpoint; the widest one is the
    // bare fallback, which is what the `sizes` grammar requires last.
    return entries
        .map((key, i) => {
            const next = entries[i + 1];
            return next ? `${below(breakpoints[next])} ${map[key]}` : `${map[key]}`;
        })
        .join(', ');
}

/**
 * `sizes` for a fluid image that fills its container up to a fixed cap — the
 * common "100% wide until the container stops growing" case. Takes the rem
 * value straight from the `contentWidth` tokens so the cap is never restated.
 *
 * @example
 * cappedSizes(contentWidth.screenshot) // '37.5rem' → "(max-width: 600px) 100vw, 600px"
 */
export function cappedSizes(maxWidthRem: string): string {
    const rem = Number.parseFloat(maxWidthRem);
    if (!Number.isFinite(rem)) {
        throw new Error(`cappedSizes() expected a rem value, got "${maxWidthRem}"`);
    }
    const px = `${rem * ROOT_FONT_SIZE_PX}px`;
    return `(max-width: ${px}) 100vw, ${px}`;
}
